import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, message, Empty } from "antd";

// ✅ API imports (path adjust karein according to your structure)
import { 
  UserTransactionsGet,
  StaffTransactionsGet,
  DevTransactionsGet,
  AdminTotalRevenueGet
} from "../../../../Api/CoreApi";

import "./RecentPayments.css";

// ✅ Helper Functions (Inline - no external dependency)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    return 'N/A';
  }
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₹0';
  return `₹${num.toLocaleString('en-IN', { 
    maximumFractionDigits: 0 
  })}`;
};

const generateRefNumber = (prefix, id) => {
  const prefixMap = { 
    'payment': 'PAY', 
    'match': 'M', 
    'user': 'U' 
  };
  const shortPrefix = prefixMap[prefix] || 'PAY';
  return `${shortPrefix}${String(id).padStart(6, '0')}`;
};

function RecentPayments({ data = null }) {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch payments from API
  useEffect(() => {
    if (data && data.length > 0) {
      // If data passed as prop, use it directly
      setPayments(data.slice(0, 5));
    } else {
      // Otherwise fetch from API
      fetchPayments();
    }
  }, [data]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      console.log("💰 Fetching payments...");

      // ✅ Try to get transactions from multiple sources
      let allTransactions = [];

      // Try User Transactions
      try {
        const userTxns = await UserTransactionsGet();
        console.log("✅ User Transactions:", userTxns?.length || 0);
        if (userTxns && userTxns.length > 0) {
          allTransactions = [...allTransactions, ...userTxns.map(t => ({
            ...t,
            source: 'user'
          }))];
        }
      } catch (err) {
        console.log("⚠️ User transactions failed:", err.message);
      }

      // Try Staff Transactions
      try {
        const staffTxns = await StaffTransactionsGet();
        console.log("✅ Staff Transactions:", staffTxns?.length || 0);
        if (staffTxns && staffTxns.length > 0) {
          allTransactions = [...allTransactions, ...staffTxns.map(t => ({
            ...t,
            source: 'staff'
          }))];
        }
      } catch (err) {
        console.log("⚠️ Staff transactions failed:", err.message);
      }

      // Try Dev Transactions
      try {
        const devTxns = await DevTransactionsGet();
        console.log("✅ Dev Transactions:", devTxns?.length || 0);
        if (devTxns && devTxns.length > 0) {
          allTransactions = [...allTransactions, ...devTxns.map(t => ({
            ...t,
            source: 'dev'
          }))];
        }
      } catch (err) {
        console.log("⚠️ Dev transactions failed:", err.message);
      }

      // Try Admin Revenue as fallback
      if (allTransactions.length === 0) {
        try {
          const adminRev = await AdminTotalRevenueGet();
          console.log("✅ Admin Revenue:", adminRev?.length || 0);
          if (adminRev && adminRev.length > 0) {
            allTransactions = adminRev.map(t => ({
              ...t,
              source: 'admin'
            }));
          }
        } catch (err) {
          console.log("⚠️ Admin revenue failed:", err.message);
        }
      }

      console.log("📊 Total transactions found:", allTransactions.length);

      if (allTransactions.length === 0) {
        setPayments([]);
        setLoading(false);
        return;
      }

      // ✅ Format transactions to payment structure
      const formattedPayments = allTransactions
        .map((txn, index) => ({
          id: txn.id || 1000 + index,
          name: txn.name || txn.username || txn.user_name || txn.customer_name || `User ${index + 1}`,
          amount: txn.amount || txn.total || txn.price || txn.value || 0,
          date: txn.date || txn.created_at || txn.transaction_date || txn.payment_date || new Date().toISOString(),
          status: getPaymentStatus(txn),
          source: txn.source,
          transaction_id: txn.transaction_id || txn.txn_id || `TXN${index}`,
        }))
        // Sort by date (newest first)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        // Take only first 5
        .slice(0, 5);

      console.log("✅ Formatted payments:", formattedPayments.length);
      setPayments(formattedPayments);

    } catch (error) {
      console.error("❌ Error fetching payments:", error);
      message.error("Failed to load payments");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Determine payment status
  const getPaymentStatus = (txn) => {
    if (txn.status) return txn.status.toLowerCase();
    if (txn.payment_status) return txn.payment_status.toLowerCase();
    
    // Random status for demo if no status field
    const statuses = ['completed', 'pending', 'completed', 'completed', 'pending'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  // ✅ Handle View All
  const handleViewAll = () => {
    navigate('/admin/transactions');
  };

  // ✅ Empty state
  if (!loading && payments.length === 0) {
    return (
      <div className="recent-payments-card">
        <div className="table-header">
          <h3 className="table-title">Recent Payments</h3>
          <button className="view-all-btn" onClick={handleViewAll}>
            View All
          </button>
        </div>
        <div className="payments-empty">
          <Empty 
            description="No payments found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="recent-payments-card">
      <div className="table-header">
        <h3 className="table-title">Recent Payments</h3>
        <button className="view-all-btn" onClick={handleViewAll}>
          View All
        </button>
      </div>

      <div className="table-wrapper">
        {loading ? (
          <div className="payments-loading">
            <Spin size="large" />
            <p>Loading payments...</p>
          </div>
        ) : (
          <table className="recent-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Payment ID</th>
                <th>Member Name</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={payment.id || index}>
                  <td>{index + 1}</td>
                  <td className="ref-id">
                    {generateRefNumber("payment", payment.id || 1000 + index)}
                  </td>
                  <td className="member-name">
                    <div className="payment-user-info">
                      <span className="payment-user-name">{payment.name}</span>
                      {payment.source && (
                        <span className={`payment-source-badge source-${payment.source}`}>
                          {payment.source}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="amount-cell">
                    {formatCurrency(payment.amount)}
                  </td>
                  <td>{formatDate(payment.date)}</td>
                  <td>
                    <span className={`status-badge status-${payment.status}`}>
                      {payment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default RecentPayments;